import { useEffect, useState, type RefObject } from 'react';
import { useIsomorphicLayoutEffect } from '~/lib/useIsomorphicLayoutEffect';

/**
 * The lateral breathing room between the artifact and the pane's edges, in screen pixels.
 *
 * Measured, not chosen: 37px each side at an 834-wide stage on five artboards, 35px at 566 and 41px
 * at 582 on two more. One eyeballed constant, so it is carried as one constant.
 */
export const ARTIFACT_GUTTER = 37;

/**
 * An image is held further off the edges than a slide — 77px each side against 37, measured on
 * `Prev/04`. The reason it is a different number rather than a bug: a slide is a document surface
 * and reads as a page, so it wants the pane; an image is raw content with no ground of its own, and
 * at the same size it reads as wallpaper rather than as a thing you are looking at.
 */
export const IMAGE_GUTTER = 77;

export interface ArtifactFitOptions {
  /** The stage. Fit is measured against its live box, so a pane resize re-fits on the next frame. */
  stageRef: RefObject<HTMLElement | null>;
  /** The artifact's own coordinate space, e.g. 1280 x 720 for a slide, 1920 x 1080 for an image. */
  naturalWidth: number;
  naturalHeight: number;
  gutter?: number;
  /**
   * Chrome drawn inside the frame but ABOVE the artifact, in screen pixels — the website kind's
   * 34px browser bar. It is not part of the artifact, so it never scales, but it does eat height
   * that the fit has to give back.
   */
  chromeHeight?: number;
  /** Multiplier on the fitted scale. 1 is `Fit`. */
  zoom?: number;
}

/**
 * The on-screen scale for the artifact, and the two numbers the frame is drawn at.
 *
 * Contain-fit rather than fit-to-width: the boards only ever show artifacts wider than they are
 * tall, so width is what binds there, but a portrait image in the same stage would run off the top
 * and bottom under a width-only rule and nothing in the design says it should.
 */
export interface ArtifactFit {
  /** Multiply the artifact's natural coordinates by this to get screen pixels. */
  scale: number;
  /** The frame's on-screen width, in px. `naturalWidth * scale`. */
  width: number;
  /** The frame's on-screen height, in px, INCLUDING `chromeHeight`. */
  height: number;
  /** False until the stage has been measured once. Nothing is drawn at a guessed size. */
  measured: boolean;
}

/** SSR and the very first client paint have no box to measure, so they get no size at all. */
const UNMEASURED: ArtifactFit = { scale: 1, width: 0, height: 0, measured: false };

/**
 * Fits the artifact to the stage and keeps it fitted.
 *
 * A `ResizeObserver` rather than a window listener: the stage changes size when the agent pane
 * collapses or a seam is dragged, and neither of those is a window resize. The observer fires on
 * the same frame as the layout that caused it, so a drag re-fits without a visible lag behind the
 * pointer.
 */
export function useArtifactFit({
  stageRef,
  naturalWidth,
  naturalHeight,
  gutter = ARTIFACT_GUTTER,
  chromeHeight = 0,
  zoom = 1,
}: ArtifactFitOptions): ArtifactFit {
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);

  // Layout effect, not effect: the artifact would otherwise paint once at zero and then jump to its
  // real size, which on a 760px card is a visible flash rather than a subtlety.
  useIsomorphicLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === 'undefined') return;

    const read = () => setBox({ w: stage.clientWidth, h: stage.clientHeight });
    read();

    const observer = new ResizeObserver(read);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [stageRef]);

  // Storybook and jsdom both run without `ResizeObserver` in some configurations, and an artifact
  // that never appears is a worse failure than one that never re-fits.
  useEffect(() => {
    if (box !== null || typeof ResizeObserver !== 'undefined') return;
    const stage = stageRef.current;
    if (stage) setBox({ w: stage.clientWidth, h: stage.clientHeight });
  }, [box, stageRef]);

  if (box === null || box.w === 0 || box.h === 0) return UNMEASURED;

  const availableWidth = Math.max(0, box.w - gutter * 2);
  const availableHeight = Math.max(0, box.h - gutter * 2 - chromeHeight);
  const fit = Math.min(availableWidth / naturalWidth, availableHeight / naturalHeight);
  const scale = fit * zoom;

  return {
    scale,
    width: naturalWidth * scale,
    height: naturalHeight * scale + chromeHeight,
    measured: true,
  };
}
